import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import API_CONFIG from './config';
import './styles/CreateUser.css';

const jobRoles = ['Admin', 'Driver', 'Manager'];

const CreateUser: React.FC<{ isAdmin: boolean }> = ({ isAdmin }) => {
    const navigate = useNavigate();
    const [checkingSession, setCheckingSession] = useState(true);

    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [jobRole, setJobRole] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [sites, setSites] = useState<{ site_id: string; site_name: string }[]>([]);
    const [selectedSites, setSelectedSites] = useState<string[]>([]);
    const [siteDropdownOpen, setSiteDropdownOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setCheckingSession(true);

        axios.get('/api/session', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
            .then(res => {
                if (cancelled) return;
                const role = res.data?.job_role;
                if (role !== 'Admin') {
                    navigate('/');
                } else {
                    setCheckingSession(false);
                }
            })
            .catch(err => {
                if (cancelled) return;
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
            });

        return () => { cancelled = true; };
    }, [navigate]);

    useEffect(() => {
        // Always fetch all sites for the dropdown
        axios.get('/api/sites', { withCredentials: true, baseURL: API_CONFIG.BASE_URL })
            .then(res => setSites(res.data || []))
            .catch(err => {
                if (err?.response?.status === 401) {
                    try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                    navigate('/login');
                } else {
                    setSites([]);
                }
            });
    }, [navigate]);

    const handleSiteChange = (siteId: string) => {
        if (selectedSites.includes(siteId)) {
            setSelectedSites(selectedSites.filter(id => id !== siteId));
        } else {
            setSelectedSites([...selectedSites, siteId]);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isAdmin) {
            setError('Only Admins can create users.');
            return;
        }
        if (!name || !email || !phone || !password || !jobRole) {
            setError('Please fill in all fields.');
            return;
        }
        if (jobRole === 'Manager' && selectedSites.length === 0) {
            setError('Please select at least one site for the manager.');
            return;
        }
        setError('');
        setSuccess('');
        try {
            const res = await axios.post('/api/create-user', {
                name,
                email,
                phone_number: phone,
                password,
                job_role: jobRole,
                site_ids: jobRole === 'Manager' ? selectedSites : undefined
            }, { withCredentials: true, baseURL: API_CONFIG.BASE_URL });
            setSuccess(res.data.message);
            setName('');
            setEmail('');
            setPhone('');
            setPassword('');
            setJobRole('');
            setSelectedSites([]);
            setSiteDropdownOpen(false);
        } catch (err: any) {
            if (err?.response?.status === 401) {
                try { sessionStorage.setItem('sessionExpired', 'true'); } catch {}
                navigate('/login');
                return;
            }
            setError(err.response?.data?.error || 'User creation failed');
        }
    };

    // Site selection UI component
    const renderSiteSelector = () => {
        if (jobRole !== 'Manager') return null;

        // Dropdown style
        const dropdownStyle: React.CSSProperties = {
            position: 'relative',
            display: 'block',
            width: '100%'
        };
        const dropdownBtnStyle: React.CSSProperties = {
            width: '100%',
            padding: 12,
            borderRadius: 6,
            border: '1px solid #ccc',
            fontSize: '1rem',
            background: '#fff',
            cursor: 'pointer',
            textAlign: 'left',
            fontWeight: 400,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
        };
        const dropdownMenuStyle: React.CSSProperties = {
            position: 'absolute',
            top: '100%',
            left: 0,
            width: '100%',
            background: '#fff',
            border: '1px solid #ccc',
            borderTop: 'none',
            borderRadius: '0 0 6px 6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            zIndex: 100,
            padding: 8,
            maxHeight: 200,
            overflowY: 'auto'
        };

        const selectedLabels = sites
            .filter(site => selectedSites.includes(site.site_id))
            .map(site => `${site.site_id} - ${site.site_name}`);

        return (
            <div style={dropdownStyle}>
                <button
                    type="button"
                    style={dropdownBtnStyle}
                    onClick={() => setSiteDropdownOpen(!siteDropdownOpen)}
                >
                    <span>
                        {selectedLabels.length === 0
                            ? 'Select Sites for Manager'
                            : selectedLabels.length === 1
                                ? selectedLabels[0]
                                : `${selectedLabels.length} sites selected`}
                    </span>
                    <span style={{ fontSize: '0.8em', color: '#888' }}>
                        {siteDropdownOpen ? '▲' : '▼'}
                    </span>
                </button>
                {siteDropdownOpen && (
                    <div style={dropdownMenuStyle}>
                        {sites.length === 0 ? (
                            <div style={{ color: '#666', fontStyle: 'italic', padding: 8, textAlign: 'center' }}>
                                Loading sites...
                            </div>
                        ) : (
                            <>
                                {sites.map(site => (
                                    <div key={site.site_id} style={{ marginBottom: 4 }}>
                                        <label
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                cursor: 'pointer',
                                                padding: '4px 0',
                                                lineHeight: 1.4,
                                                gap: 8
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={selectedSites.includes(site.site_id)}
                                                onChange={() => handleSiteChange(site.site_id)}
                                                style={{ accentColor: '#115740' }}
                                            />
                                            <span style={{ fontWeight: 600, minWidth: 80 }}>
                                                {site.site_id}
                                            </span>
                                            <span style={{ color: '#666' }}>
                                                - {site.site_name}
                                            </span>
                                        </label>
                                    </div>
                                ))}
                                <div style={{ borderTop: '1px solid #eee', marginTop: 8, paddingTop: 8 }}>
                                    <button
                                        type="button"
                                        style={{
                                            width: '100%',
                                            padding: '6px 12px',
                                            borderRadius: 4,
                                            border: 'none',
                                            background: '#f0f0f0',
                                            color: '#666',
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            fontSize: '0.9em'
                                        }}
                                        onClick={() => setSiteDropdownOpen(false)}
                                    >
                                        Close
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (checkingSession) return <div />;

    return (
        <div
            style={{
                width: '100%',
                minHeight: '100vh',
                background: '#f5f6fa',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <div
                className="create-user-wrapper"
                style={{
                    maxWidth: 1500,
                    margin: '40px auto',
                    minHeight: 'calc(100vh - 80px)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: '100%',
                        marginBottom: 48,
                        height: 72,
                        position: 'relative'
                    }}
                >
                    <div style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <h2 style={{
                            margin: 0,
                            fontSize: '2.4em',
                            fontWeight: 700,
                            letterSpacing: '0.5px'
                        }}>
                            Create New User
                        </h2>
                    </div>
                </div>
                <div className="create-user-dialog" style={{
                    background: '#fff',
                    padding: '32px',
                    borderRadius: '12px',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
                    minWidth: 500,
                    maxWidth: 600,
                    width: '100%'
                }}>
                    <h3 style={{ marginBottom: 18 }}>Create New User</h3>
                    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        <input
                            type="text"
                            placeholder="Full Name"
                            value={name}
                            onChange={e => setName(e.target.value)}
                            style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                            required
                        />
                        <input
                            type="email"
                            placeholder="Email Address"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                            required
                        />
                        <input
                            type="text"
                            placeholder="Phone Number"
                            value={phone}
                            onChange={e => setPhone(e.target.value)}
                            style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                            required
                        />
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                            required
                        />
                        <select
                            value={jobRole}
                            onChange={e => setJobRole(e.target.value)}
                            style={{ padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                            required
                        >
                            <option value="" disabled>Select Job Role</option>
                            {jobRoles.map(role => (
                                <option key={role} value={role}>{role}</option>
                            ))}
                        </select>
                        {renderSiteSelector()}
                        <div style={{ display: 'flex', gap: 12 }}>
                            <button
                                type="submit"
                                style={{
                                    padding: '10px 22px',
                                    borderRadius: 6,
                                    background: '#115740',
                                    color: '#fff',
                                    border: 'none',
                                    fontWeight: 600
                                }}
                            >
                                Create User
                            </button>
                            <button
                                type="button"
                                style={{
                                    padding: '10px 22px',
                                    borderRadius: 6,
                                    background: '#eee',
                                    color: '#333',
                                    border: 'none',
                                    fontWeight: 500
                                }}
                                onClick={() => navigate('/')}
                            >
                                Cancel
                            </button>
                        </div>
                        {success && <div style={{ color: '#115740', fontWeight: 500 }}>{success}</div>}
                        {error && <div style={{ color: '#d32f2f', fontWeight: 500 }}>{error}</div>}
                    </form>
                </div>
            </div>
        </div>
    );
};

export default CreateUser;